import React, { useEffect, useRef, useState } from 'react';
import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { uniqBy, orderBy } from 'lodash';
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import Papa from 'papaparse';
import { useRouterParams } from 'src/hooks/utils';
import { PublicSurveyIntro, PublicSurveyQuestion } from 'src/components/PublicSurveyComponent';
import { notification } from 'src/components/Notification';
import { applyQuestionConditions, getDependentQuestionsOnParentQuestion, getValidationTextBasedOnLanguage } from 'src/components/JobDatasheet/JobDatasheetForm/utils';
import { JobDatasheetLanguageProvider } from 'src/components/JobDatasheet/context/JobDatasheetLanguageContext';
import { SURVEY_RESPONSES_BY_HASH } from 'src/graphql/survey/query';
import client from 'src/utils/api/client';
import Loader from 'src/components/Loader';
import SelectBox from 'src/components/SelectBox';
import { setSurveyResult, getSurveyResult } from 'src/components/PublicSurveyComponent/utils/localStorage';
import PublicSurveyLanguage from 'src/components/PublicSurveyComponent/PublicSurveyLanguage';
import { useGetSurveyDataByCodeLazy } from 'src/components/PublicSurveyComponent/hooks';
import getValidationErrorsForQuestion from './utils/getValidationErrorsForQuestion';
import { useAddSurveyResponse } from './hooks';
import S from './PublicSurveyPage.styles';

const PublicSurveyPage = props => {
  const { code } = useRouterParams(props.match);
  const { t, i18n } = useTranslation();
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [surveyState, setSurveyState] = useState({
    started: 0,
    ended: 0,
  });
  const [surveySkin, setSurveySkin] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [visibleSurveyQuestions, setVisibleSurveyQuestions] = useState([]);
  const [currentActiveQuestionIndex, setCurrentActiveQuestionIndex] = useState(0);
  const [surveyExecutionHash, setSurveyExecutionHash] = useState(null);
  const [questionResultsMap, setQuestionResultsMap] = useState(new Map());
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [questionResultValue, setQuestionResultValue] = useState(null);
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(0);
  const [showSurveyIntro, setShowSurveyIntro] = useState(false);
  const [showSurveyLanguage, setShowSurveyLanguage] = useState(true);
  const [fpVisitorId, setFpVisitorId] = useState('');
  const pageVisibilityTimes = useRef([]);
  const visibleSurveyQuestionsRef = useRef([]);
  const surveyTimeKey = `survey-${code}-time`;
  const addSurveyResponse = useAddSurveyResponse();
  const surveyLinkToken = code;
  const surveyViewedKey = `survey-${surveyLinkToken}-viewed`;
  const [activateQuestionAfterRequest, setActivateQuestionAfterRequest] = useState();
  const [responseLoading, setResponseLoading] = useState(false);
  const [isSecondResponseError, setIsSecondResponseError] = useState(false);
  const [surveyLoaded, setSurveyLoaded] = useState(showSurveyIntro || false);
  const [validationTranslations, setValidationTranslations] = useState(null);
  const [navigationButtonTranslations, setNavigationButtonTranslations] = useState(null);

  const setActiveQuestionNextAfter = datasheetQuestionId => {
    setActivateQuestionAfterRequest(datasheetQuestionId);
  };
  useEffect(() => {
    if (!activateQuestionAfterRequest) {
      return;
    }

    const index = visibleSurveyQuestions.findIndex(q => q.datasheetQuestionId === activateQuestionAfterRequest);
    setCurrentActiveQuestionIndex(index + 1);
  }, [activateQuestionAfterRequest]);

  useEffect(() => {
    const getFingerPrintVisitorId = async () => {
      const fp = await FingerprintJS.load();

      const { visitorId } = await fp.get();
      setFpVisitorId(visitorId);
    };

    const getTranslationValidationFile = async () => {
      Papa.parse(SURVEY_VALIDATION_TRANSLATION_URL, { download: true, header: true, complete: (result) => setValidationTranslations(result.data), error: () => setValidationTranslations(null) });
    };

    getFingerPrintVisitorId();
    getTranslationValidationFile();
  }, []);

  const loadResponses = async hash => {
    try {
      setResponseLoading(true);
      const response = await client.query({
        query: SURVEY_RESPONSES_BY_HASH,
        variables: {
          hash,
        },
      });

      const rows = response.data.surveyResponsesByHash.results;
      const localQuestionResultsMap = new Map();
      rows.forEach(row => {
        localQuestionResultsMap.set(row.datasheetQuestionId, row.result);
      });
      setQuestionResultsMap(localQuestionResultsMap);
      setSurveyExecutionHash(hash);
      setSurveyState({ started: 1, ended: 0 });

      if (rows.length > 0) {
        setActiveQuestionNextAfter(rows[rows.length - 1]?.datasheetQuestionId);
      }
    } finally {
      setResponseLoading(false);
    }
  };

  const [loadSurveyDataByCodeLazy, { data, loading, error }] = useGetSurveyDataByCodeLazy({
    code: surveyLinkToken,
    visitorId: fpVisitorId,
  });

  useEffect(() => {
    if(fpVisitorId){
      loadSurveyDataByCodeLazy();
    }
  }, [fpVisitorId]);

  useEffect(() => {
    if (data && data.surveyByCode) {
      const storedResult = getSurveyResult(surveyLinkToken);
      if (data.surveyByCode.surveyData.isDuplicateVisitorId && data.surveyByCode.surveyData.restrictToOneResponse && data.surveyByCode.surveyData.isFinished) {
        setIsSecondResponseError(true);
      } else if (!data.surveyByCode.surveyData.isFinished && 
        data.surveyByCode.surveyData.allowToResume && 
        data.surveyByCode.surveyData.restrictToOneResponse && 
        storedResult?.hash) {
        setResponseLoading(true);
        loadResponses(storedResult.hash);
      }
      setSurveyLoaded(true);
      setSurveySkin(data.surveyByCode.surveySkin);
      setSurveyData(data.surveyByCode.surveyData);
      window.localStorage.setItem(surveyViewedKey, 'true');
    }
  }, [loading]);


  useEffect(() => {
    if (surveyData && surveyData.questions.length) {
      setSurveyQuestions([...surveyData.questions]);
    }
  }, [JSON.stringify(surveyData)]);

  useEffect(() => {
    if (surveyData && surveyData.questions.length) {
      const newQuestResultsMap = new Map(questionResultsMap);
      surveyData.questions.forEach(({ datasheetQuestionId }) => {
        if (!newQuestResultsMap.get(datasheetQuestionId)) {
          newQuestResultsMap.set(datasheetQuestionId, null);
        }
      });
      setQuestionResultsMap(newQuestResultsMap);
    }
  }, [JSON.stringify(surveyData)]);

  const getVisibleSurveyQuestions = (resultsMap) => {
    const nextVisibleSurveyQuestions = applyQuestionConditions(
      {
        questions: surveyQuestions,
      },
      question => {
        return {
          result: resultsMap.get(question.datasheetQuestionId),
        };
      },
      null,
      false,
    );
    return nextVisibleSurveyQuestions;
  };

  useEffect(() => {
    const nextVisibleSurveyQuestions = getVisibleSurveyQuestions(questionResultsMap);
    visibleSurveyQuestionsRef.current = nextVisibleSurveyQuestions;
    setVisibleSurveyQuestions(nextVisibleSurveyQuestions);
  }, [JSON.stringify(surveyQuestions), JSON.stringify(Array.from(questionResultsMap.entries()))]);

  useEffect(() => {
    if (!surveyState.ended && surveyState.started) {
      // set start time for the first question
      setCurrentQuestionStartTime(Date.now());
    }
  }, [surveyState.started]);

  const onPageVisibilityChange = () => {
    if (document.hidden) {
      // navigated away at time
      pageVisibilityTimes.current.push([Date.now()]);
    } else {
      const len = pageVisibilityTimes.current.length;
      if (len) {
        // returned at time
        pageVisibilityTimes.current[len - 1].push(Date.now());
      }
    }
  };

  useEffect(() => {
    if (surveyState.started) {
      document.addEventListener('visibilitychange', onPageVisibilityChange);
    }
    return () => {
      document.removeEventListener('visibilitychange', onPageVisibilityChange);
    };
  }, [surveyState.started]);

  const availableLanguages = orderBy(
    uniqBy(
      [
        {
          value: null,
          label: 'English',
          code: 'en',
        },
        ...(surveyData?.languages || []).map(language => ({
          value: language.id,
          label: language.name,
          code: language.code,
        })),
      ],
      'value',
    ),
    [
      language => {
        return language.value === null ? '' : language.label;
      },
    ],
    ['asc'],
  );

  const setQuestionResult = (value, datasheetQuestionId) => {
    if (!saveInProgress) {
      const newQuestResultsMap = new Map(questionResultsMap);
      newQuestResultsMap.set(datasheetQuestionId, value);
      setQuestionResultsMap(newQuestResultsMap);
      setQuestionResultValue(value);
    }
  };

  const getQuestionResult = ({ datasheetQuestionId }) => {
    if (questionResultsMap.has(datasheetQuestionId)) {
      // wrapped in object to match already existing usage in existing component
      return {
        result: questionResultsMap.get(datasheetQuestionId),
      };
    }
    return null;
  };

  const getDependentQuestionsByDatasheetQuestionId = (datasheetQuestionId) => {
    return getDependentQuestionsOnParentQuestion(surveyQuestions, datasheetQuestionId);
  };

  const getUpdatedSurveyResultsForHiddenQuestions = (dependentQuestions) => {
    const visibleQuestionDatasheetIds = visibleSurveyQuestionsRef.current.map(q => q.datasheetQuestionId);
    const hiddenQuestions = dependentQuestions.filter(q => !visibleQuestionDatasheetIds.includes(q.datasheetQuestionId));
    
    const newQuestionResultsMap = new Map(questionResultsMap);
    hiddenQuestions.forEach(que => {
      newQuestionResultsMap.set(que.datasheetQuestionId, null);
    });
    return newQuestionResultsMap;
  };

  const getNextVisibleQuestionsOnDependentQuestions = (dependentQuestions) => {
    const newQuestionResultsMap = getUpdatedSurveyResultsForHiddenQuestions(dependentQuestions);
    const nextVisibleQuestions = getVisibleSurveyQuestions(newQuestionResultsMap);
    setQuestionResultsMap(newQuestionResultsMap);
    return nextVisibleQuestions;
  };
  useEffect(() => {
    const en = i18n.getFixedT('en');
    const selectedLanguageCode = availableLanguages.find(a => a.value === selectedLanguage)?.code || 'en';
    const defaultNavigationTranslation = [en('NEXT'), en('PREVIOUS'),en('START'), en('FINISH'), en('PLEASE_WAIT')]
    const navigationTranslations = getValidationTextBasedOnLanguage(defaultNavigationTranslation, validationTranslations, selectedLanguageCode);
     const navTranslations = {
      next: navigationTranslations[0],
      previous: navigationTranslations[1],
      start: navigationTranslations[2],
      finish: navigationTranslations[3],
      wait: navigationTranslations[4],
    }
    setNavigationButtonTranslations(navTranslations);  
  }, [selectedLanguage]);
  const moveToNextQuestion = async () => {
    setSaveInProgress(true);
    const currentQuestion = visibleSurveyQuestions[currentActiveQuestionIndex];
    const { datasheetQuestionId } = currentQuestion;
    const result = questionResultsMap.get(datasheetQuestionId);

    const validationErrors = getValidationErrorsForQuestion(currentQuestion, questionResultsMap);
    if (validationErrors.length > 0) {
      const selectedLanguageCode = availableLanguages.find(a => a.value === selectedLanguage)?.code || 'en';
      const newValidationErrors = getValidationTextBasedOnLanguage(validationErrors, validationTranslations, selectedLanguageCode);
      notification.error(newValidationErrors.join(', '));
      setSaveInProgress(false);
      return;
    }
    const dependentQuestions = getDependentQuestionsByDatasheetQuestionId(datasheetQuestionId);
    let currentVisibleSurveyQuestions = visibleSurveyQuestionsRef.current;
    if (dependentQuestions.length) {
      currentVisibleSurveyQuestions = getNextVisibleQuestionsOnDependentQuestions(dependentQuestions);
    }
    const isFinished = currentActiveQuestionIndex === currentVisibleSurveyQuestions.length - 1;
    try {
      const currentQuestionFinishTime = Date.now();
      const timeTaken = currentQuestionFinishTime - currentQuestionStartTime;
      const totalTimeInactiveInSeconds = Math.round(
        pageVisibilityTimes.current
          .map(pvt => {
            return (pvt[1] || 0) - (pvt[0] || 0);
          })
          .reduce((a, b) => a + b, 0) / 1000,
      );

      const timeTakenTillNow = Number.parseInt(window.localStorage.getItem(surveyTimeKey), 10) || 0;
      const timeTakenForCurrentQuestion = Math.round(timeTaken / 1000 - totalTimeInactiveInSeconds);
      const totalCurrentTimeTakenTillNow = timeTakenTillNow + timeTakenForCurrentQuestion;
      window.localStorage.setItem(surveyTimeKey, `${totalCurrentTimeTakenTillNow}`);

      const response = await addSurveyResponse({
        hash: surveyExecutionHash,
        datasheetQuestionId,
        result: JSON.stringify(result || null),
        isFinished,
        responseTime: totalCurrentTimeTakenTillNow,
        removeResultQuestionIds: dependentQuestions.map(que => que.datasheetQuestionId),
      });
      if (!isFinished) {
        setCurrentActiveQuestionIndex(prevState => prevState + 1);
        // set start time for subsequent questions
        setCurrentQuestionStartTime(Date.now());
        pageVisibilityTimes.current = [];
      } else {
        window.localStorage.removeItem(surveyTimeKey);
        setSurveyResult(code, surveyExecutionHash, true);

        if (surveyData.prizeDrawUrl) {
          window.location.href = surveyData.prizeDrawUrl;
        } else {
          setSurveyState({ started: 1, ended: 1 });
        }
      }
    } catch (err) {
      notification.error(t('FAILED_TO_SAVE_QUESTION_RESPONSE'));
    }
    setSaveInProgress(false);
  };

  const isAutoAdvanceQuestion = (type, value) => {
    return (
      ((type === 'dropdown' ||
        type === 'dependentDropdown' ||
        type === 'picture' ||
        type === 'voice' || type === 'radioButton') &&
        !Array.isArray(value)) ||
      type === 'smileyRating'
    );
  };

  useEffect(() => {
    if (!saveInProgress) {
      const isFinished = currentActiveQuestionIndex === visibleSurveyQuestionsRef.current.length - 1;
      if (!isFinished && questionResultValue && !Object.values(questionResultValue).includes(null)) {
        const { type, value } = questionResultValue;
        // PHX-3814 Deactivate auto advance for surveys
        // if (isAutoAdvanceQuestion(type, value)) {
        //   moveToNextQuestion();
        // }
      }
    } 

  }, [questionResultValue]);

  useEffect(() => {
    if (currentActiveQuestionIndex > 0 && currentActiveQuestionIndex >= visibleSurveyQuestions.length) {
      setCurrentActiveQuestionIndex(currentActiveQuestionIndex - 1);
    }
  }, [currentActiveQuestionIndex, visibleSurveyQuestions]);

  const getSkinWithSupportedLanguage = skinString => {
    if (typeof skinString !== 'string') {
      return '';
    }
  
    const selectedLanguageCode = availableLanguages.find(a => a.value === selectedLanguage)?.code || 'en';
  
    let decodedString = skinString
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&rdquo;/g, "'");
  
    decodedString = decodedString.replace(/<language=["']?([^"'>]+)["']?>([\s\S]*?)<\/language>/gi, (match, lang, content) => {
      return lang.toLowerCase() === selectedLanguageCode.toLowerCase() ? content : "";
    });
  
    return decodedString;
  };
  
  if (error) {
    let message = error.message.replace('GraphQL error: ', '');
    if (message === 'Survey ended') {
      message = t('SURVEY_ENDED_ERROR');
    }

    return (
      <S.Page>
        <S.Container>
          <S.ContainerInner>
            <S.FullHeightContent>
              <S.Ack>
                <S.AckInner overflow-hidden>
                  <S.AckLogoContainer>
                    <S.AckLogo name="logo-elevate" />
                  </S.AckLogoContainer>
                  <S.AckInnerLabel space-below>
                    <br />
                    {message}
                  </S.AckInnerLabel>
                </S.AckInner>
              </S.Ack>
            </S.FullHeightContent>
          </S.ContainerInner>
        </S.Container>
      </S.Page>
    );
  }

  if (loading || responseLoading || !surveyLoaded) {
    return <Loader withoutLabel />;
  }

  if (isSecondResponseError) {
    return (
      <S.Page>
        <S.Container>
          <S.ContainerInner>
            <S.FullHeightContent>
              <S.Ack>
                <S.AckInner>
                  <S.AckLogoContainer>
                    <S.AckLogo name="logo-elevate" />
                  </S.AckLogoContainer>
                  <S.AckInnerLabel space-below>
                    <br />
                    {t('SURVEY_RESTRICT_SECOND_RESPONSE')}
                    <br />
                    <br />
                    {t('SURVEY_THANK_YOU')}
                  </S.AckInnerLabel>
                </S.AckInner>
              </S.Ack>
            </S.FullHeightContent>
          </S.ContainerInner>
        </S.Container>
      </S.Page>
    );
  }

  return (
    <S.Page>
      <S.Container>
        <S.ContainerInner>
          <S.FullHeightContent>
            {(showSurveyIntro || (surveyState.ended === 0 && surveyState.started === 0)) && (
              <PublicSurveyIntro
                setIsSecondResponseError={setIsSecondResponseError}
                setSurveyData={setSurveyData}
                setSurveyExecutionHash={setSurveyExecutionHash}
                setQuestionResultsMap={setQuestionResultsMap}
                setActiveQuestionNextAfter={setActiveQuestionNextAfter}
                setSurveySkin={setSurveySkin}
                setSurveyState={setSurveyState}
                surveyLinkToken={code}
                showSurveyIntro={showSurveyIntro}
                setShowSurveyIntro={setShowSurveyIntro}
                surveySkin={surveySkin}
                showSurveyLanguage={showSurveyLanguage}
                setShowSurveyLanguage={setShowSurveyLanguage}
                availableLanguages={availableLanguages}
                setSelectedLanguage={setSelectedLanguage}
                selectedLanguage={selectedLanguage}
                onLanguageNext={() => setShowSurveyLanguage(false)}
                getSkinWithSupportedLanguage={getSkinWithSupportedLanguage}
                visitorId={fpVisitorId}
                navigationButtonTranslations={navigationButtonTranslations}
              />
            )}
            {!showSurveyIntro && surveyState.ended === 0 && surveyState.started === 1 && (
              <div>
                {showSurveyLanguage && availableLanguages.length > 1 ? (
                  <PublicSurveyLanguage
                    availableLanguages={availableLanguages}
                    setSelectedLanguage={setSelectedLanguage}
                    selectedLanguage={selectedLanguage}
                    onLanguageNext={() => setShowSurveyLanguage(false)}
                    navigationButtonTranslations={navigationButtonTranslations}
                  />
                ) : (
                  <JobDatasheetLanguageProvider value={{ selectedLanguage, availableLanguages }}>
                    <S.ContentContainer>
                    <S.PublicSurveyContentContainer>
                    {availableLanguages.length > 1 && (
                      <S.LanguageSelectContainer>
                        <S.LanguageSelectWrapper>
                          <SelectBox
                            isSearchable={false}
                            options={availableLanguages}
                            onChange={e => setSelectedLanguage(e.value)}
                            value={availableLanguages.find(c => c.value === selectedLanguage)}
                          />
                        </S.LanguageSelectWrapper>
                      </S.LanguageSelectContainer>
                    )}
                    <S.HeaderWrapper>
                      {parse(getSkinWithSupportedLanguage(surveySkin?.header || ''))}
                    </S.HeaderWrapper>
                    <ProgressBar
                      baseBgColor={'white'}
                      bgColor={'#f26f23'}
                      completed={Math.round(((currentActiveQuestionIndex + 1) * 100) / visibleSurveyQuestions.length)}
                      className="progressbar"
                      isLabelVisible={false}
                    />
                    <S.QuestionWrapper>
                      {(visibleSurveyQuestions || []).map((question, index) => (
                        <PublicSurveyQuestion
                          key={`${question.datasheetQuestionId}-${index}`}
                          allQuestions={visibleSurveyQuestions}
                          currentActiveQuestionIndex={currentActiveQuestionIndex}
                          getQuestionResult={getQuestionResult}
                          question={question}
                          questionIndex={index}
                          questionResultsMap={questionResultsMap}
                          setQuestionResult={setQuestionResult}
                          surveyExecutionHash={surveyExecutionHash}
                          disableChange={saveInProgress}
                        />
                      ))}
                    </S.QuestionWrapper>
                    </S.PublicSurveyContentContainer>
                    <S.FooterButtonsWrapper>
                    <S.ButtonsWrapper>
                      <div>
                        {currentActiveQuestionIndex !== 0 ? (
                          <S.SurveyActionButton
                            onClick={() => setCurrentActiveQuestionIndex(prevState => prevState - 1)}
                          >
                            {<><S.SurveyActionLogo name="survey-previous" /> {navigationButtonTranslations.previous}</>}
                          </S.SurveyActionButton>
                        ) : (
                          <S.SurveyActionButton onClick={() => setShowSurveyIntro(true)}>
                            {<><S.SurveyActionLogo name="survey-previous" /> {navigationButtonTranslations.previous}</>}
                          </S.SurveyActionButton>
                        )}
                      </div>
                      <div>
                        <S.SurveyActionButton
                          onClick={saveInProgress ? () => {} : moveToNextQuestion}
                          disabled={saveInProgress}
                        >
                          {currentActiveQuestionIndex !== visibleSurveyQuestions.length - 1 ? (
                            <>{navigationButtonTranslations.next} <S.SurveyActionLogo name="survey-next" /></>
                          ) : (
                            <>{navigationButtonTranslations.finish} <S.SurveyActionLogo name="survey-finish" /></>
                          )}
                        </S.SurveyActionButton>
                      </div>
                    </S.ButtonsWrapper>
                    </S.FooterButtonsWrapper>
                    </S.ContentContainer>
                  </JobDatasheetLanguageProvider>
                )}
              </div>
            )}
            {!showSurveyIntro && surveyState.ended === 1 && (
              <>
                {showSurveyLanguage && availableLanguages.length > 1 ? (
                  <PublicSurveyLanguage
                    availableLanguages={availableLanguages}
                    setSelectedLanguage={setSelectedLanguage}
                    selectedLanguage={selectedLanguage}
                    onLanguageNext={() => setShowSurveyLanguage(false)}
                    navigationButtonTranslations={navigationButtonTranslations}
                  />
                ) : (
                  <S.Ack>
                    <S.AckInner>
                      <S.AckLogoContainer>
                        <S.AckLogo name="logo-elevate" />
                      </S.AckLogoContainer>
                      <S.AckInnerLabel space-below space-above>
                        {parse(getSkinWithSupportedLanguage(surveySkin?.acknowledgmentTemplate || ''))}
                      </S.AckInnerLabel>
                    </S.AckInner>
                  </S.Ack>
                )}
              </>
            )}
          </S.FullHeightContent>
        </S.ContainerInner>
      </S.Container>
    </S.Page>
  );
};

export default React.memo(PublicSurveyPage);
